import React from 'react';
import { withRouter } from 'react-router-dom';
import CustomCircularProgress from '../common/CustomCircularProgress';

import authService from '../api-authorization/AuthorizeService';
import {
    FormLabel,
    FormBlocker,
    CollapseUnderlineHeader,
    FlexCenterRow,
    FlexBetweenRow,
    FlexColumnStart,
    FormValidated,
    SmallButton,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    MomentTimeFormat,
} from '../Common';
import { util } from '../Util';
import {
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Container,
    Button,
    Modal,
    ModalHeader,
    ModalFooter,
    ModalBody
} from 'reactstrap';
import './DailyDrivingLogForm.scss';
import TimeEntry from '../common/forms/TimeEntry';
import * as moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faCircleNotch,
    faPlus,
} from '@fortawesome/free-solid-svg-icons';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    SelfDispatchTimesheet,
    SelfDispatchTimesheetDetailViewModel
} from './DailyDrivingLog';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { ChargeTypeUnits } from '../chargeType/ChargeType';

class DailyDrivingLogForm extends React.Component {

    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const selectedDate = this.props.match.params.id;

        this.state = {
            loading: true,
            saving: false,
            selectedDate: selectedDate,
            jobsOpen: true,
            chargesOpen: true,
            summaryJobsOpen: false,
            summaryChargesOpen: true,
            showAddChargeModal: false,
            showSubmitChargesModal: false,
            showRemoveInternalChargeModal: false,
            addTimeFormValidated: false,
            editable: false,
            removeChargeId: null,
            hourlyJobs: false,
            tonnageJobs: false,
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    async populateState() {
        const { selectedDate } = { ...this.state };
        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            return;
        }

        const tenantUserProfile = await getTenantUserProfile();

        let [dailyEntries, nonFlagContract, contracts, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byDate(selectedDate)),
            util.fetch.js(ApiRoutes.contract.getNonFlagging()),
            util.fetch.js(ApiRoutes.contract.getSelfDispatching()),
            util.fetch.js(ApiRoutes.dailyDrivingLog.getEmployeeEquipment()),
        ]);

        var timesheet = new SelfDispatchTimesheet();
        timesheet.contractId = nonFlagContract.id;
        timesheet.contractDescription = nonFlagContract.description;
        timesheet.nonFlagging = true;
        contracts = [nonFlagContract];

        const userGroups = [
            ...new Set(
                tenantUserProfile.currentUser.appGroups.map(
                    (v) => v.groupName,
                ),
            ),
        ];

        contracts.forEach((contract) => {
            const chargetypes = contract.contractChargeTypes;
            contract.contractChargeTypes = chargetypes.filter((ct) => userGroups.includes(ct.group));
        });

        this.setState((state) => ({
            loading: false,
            saving: false,
            hourlyJobs: false,
            tonnageJobs: false,
            dailyEntries: dailyEntries,
            nonFlagContract,
            contracts,
            equipment,
            timesheet,
            driverName: tenantUserProfile.currentUser.fullName
        }));
    }

    refresh = () => {
        this.populateState();
    }

    onTimeChange = (ev, charge) => {
        const { dailyEntries } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, [MomentTimeFormat.AnteTwelveHour]).format(
                MomentTimeFormat.TwentyFourHourWithLeading,
            )
            : '';

        charge[name] = timeValue;

        this.setState((state) => ({
            dailyEntries: dailyEntries
        }));

        // TODO: Save changes
    };

    onEntryClicked = (event) => {
        if (event.isConfirmed && event.timesheetId > 0)
            this.props.history.push(`${AppNavPaths.Timesheet}/${event.timesheetId}`);
        else
            this.props.history.push(`${AppNavPaths.AssignmentConfirmation}/${event.assignmentId}`);    
    };

    showSubmitChargesModal = () => {
        this.setState((state) => ({
            showSubmitChargesModal: true
        }));
    };

    submitInternalCharges = async () => {
        const { selectedDate } = { ...this.state };

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.submitInternalCharges(selectedDate),
        );

        this.populateState();

        this.setState((state) => ({
            showSubmitChargesModal: false
        }));
    }

    onAddChargeClicked = () => this.setState({
        editingTimeIndex: null,
        showAddChargeModal: true,
        selectedDetail: new SelfDispatchTimesheetDetailViewModel(),
        addTimeFormValidated: false,
    });

    onEditChargeClicked = (idx, selection) => {

        if (selection.isConfirmed)
            return;

        var selectedDetail = new SelfDispatchTimesheetDetailViewModel();

        selectedDetail.timesheetId = selection.timesheetId;
        selectedDetail.id = selection.id;
        selectedDetail.chargeTypeId = selection.chargeTypeId;
        selectedDetail.chargeTypeName = selection.chargeTypeName;
        selectedDetail.chargeTypeUnitsId = 1;
        selectedDetail.chargeTypeUnitsName = 'Hours';
        selectedDetail.start = selection.startTime;
        selectedDetail.end = selection.endTime;
        selectedDetail.equipmentId = selection.equipmentId;
        selectedDetail.equipmentName = selection.equipmentName;

        this.setState({
            editingTimeIndex: idx,
            showAddChargeModal: true,
            selectedDetail,
            addTimeFormValidated: false,
        });
    };

    onChargeTypeChanged = async (selection) => {
        const { selectedDetail } = { ...this.state };

        if (!selection) {
            selectedDetail.start = null;
            selectedDetail.end = null;
            selectedDetail.chargeTypeId = null;
            selectedDetail.chargeTypeUnitsId = null;
            selectedDetail.chargeTypeUnitsName = null;
        } else {
            selectedDetail.chargeTypeId = selection.chargeTypeId;
            selectedDetail.chargeTypeName = selection.description;
            const chargeTypeUnitsId = selection.chargeUnits === 'Hours' ? ChargeTypeUnits.Hours
                : selection.chargeUnits === 'Flat' ? ChargeTypeUnits.Flat
                    : ChargeTypeUnits.Tonnage;
            selectedDetail.chargeTypeUnitsId = chargeTypeUnitsId;
            selectedDetail.chargeTypeUnitsName = selection.chargeUnits;
        }

        if (selection.equipment) {
            await this.setState({ equipmentLoading: true });
            const equipmentOptions = await util.fetch.js(
                ApiRoutes.selfDispatchTimesheet.equipmentByChargeType(
                    selection.chargeTypeId,
                ),
            );
            this.setState({ equipmentOptions, equipmentLoading: false });
        }
        this.setState({ selectedDetail });
    };

    onEquipmentChanged = async (selection) => {
        const { selectedDetail } = { ...this.state };

        selectedDetail.equipmentId = selection.id;
        selectedDetail.equipmentName = selection.description;

        this.setState({ selectedDetail, selection });
    };

    getDuration = (date, start, end, breakStart, breakEnd, bPrint) => {
        // Print duration between start and end time
        const totalDuration = '0h 00m';
        const dateFormat = 'YYYY-MM-DD HH:mm';

        if (start && end) {
            const mDate = moment(date).format('YYYY-MM-DD');
            const strStart = `${mDate} ${start}`;
            const strEnd = `${mDate} ${end}`;

            const mStartTime = moment(strStart, dateFormat);
            const mEndTime = moment(strEnd, dateFormat);

            // Account for times stretching into the next morning/day.
            if (mEndTime.isBefore(mStartTime)) {
                mEndTime.add(1, 'day');
            }

            const duration = moment.duration(mEndTime.diff(mStartTime));

            let breakDurationString = '';

            if (breakStart && breakEnd) {
                const breakStartString = `${mDate} ${breakStart}`;
                const breakEndString = `${mDate} ${breakEnd}`;

                const breakStartTime = moment(breakStartString, dateFormat);
                const breakEndTime = moment(breakEndString, dateFormat);

                if (breakEndTime.isBefore(breakStartTime)) {
                    breakEndTime.add(1, 'day');
                }

                const breakDuration = moment.duration(
                    breakEndTime.diff(breakStartTime),
                );

                duration.subtract(breakDuration);

                const breakTotalMinutes = parseInt(breakDuration.asMinutes());

                const breakHours = Math.floor(breakTotalMinutes / 60);
                const breakMinutes = (breakTotalMinutes % 60)
                    .toString()
                    .padStart(2, '0');

                breakDurationString = breakHours > 0
                    ? ` (${breakHours}h ${breakMinutes}m break)`
                    : ` (${breakMinutes}m break)`;
            }

            const totalMinutes = parseInt(duration.asMinutes());

            if (totalMinutes >= 0) {
                const hours = Math.floor(totalMinutes / 60);
                const mins = (totalMinutes % 60).toString().padStart(2, '0');

                if (bPrint) return `${hours}h ${mins}m${breakDurationString}`;
                return totalMinutes;
            }
        }
        return bPrint ? totalDuration : 0.0;
    };

    onChargeChange = (ev) => {
        const { selectedDetail } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, [MomentTimeFormat.AnteTwelveHour]).format(
                MomentTimeFormat.TwentyFourHourWithLeading,
            )
            : '';

        selectedDetail[name] = name === 'value' ? val : timeValue;

        if (['breakStart', 'breakEnd'].some((v) => v == name) && !timeValue) {
            selectedDetail.breakStart = '';
            selectedDetail.breakEnd = '';
        }

        this.setState({
            selectedDetail,
            addTimeFormValidated: false /* reset validation to avoid highlights when changing info */,
        });
    };

    timeEntryOverlaps = (entry, timeEntries) => {
        let overlaps = false;

        if (!timeEntries.length) return overlaps;

        timeEntries.sort(this.compareTimeDetailVm);

        const entryStart = this.formatTime24Hr(entry.start);
        const entryEnd = this.formatTime24Hr(entry.end);
        for (const e of timeEntries) {
            const start = this.formatTime24Hr(e.start);
            const end = this.formatTime24Hr(e.end);
            if (
                // Test the new entry against the iterated item
                (entryStart.isAfter(start) && entryStart.isBefore(end))
                || (entryEnd.isAfter(start) && entryEnd.isBefore(end))
                // Test the iterated item against the new entry.
                // Must be done in case the new entry is outside the bounds of the iterated item
                // e.g. 12AM - 6PM against 6:30AM - 12PM
                || (start.isAfter(entryStart) && start.isBefore(entryEnd))
                || (end.isAfter(entryStart) && end.isBefore(entryEnd))
            ) {
                overlaps = true;
                break;
            }
        }
        return overlaps;
    };

    formatTime24Hr = (stringNumber) => moment(stringNumber, MomentTimeFormat.TwentyFourHourWithLeading);

    onUpdateCharge = async (e) => {

        const {
            timesheet, contracts, selectedDetail, editingTimeIndex, selectedDate
        } = {
            ...this.state,
        };

        const selectedContract = (contracts ?? []).find(
            (x) => x.id === timesheet?.contractId,

        );
        const selectedContractChargeType = (selectedContract?.contractChargeTypes ?? []).find(
            (x) => x.chargeTypeId === selectedDetail?.chargeTypeId,

        ) ?? '';

        const creatingNew = Boolean(Number.isNaN(parseInt(editingTimeIndex)));
        timesheet.date = moment(selectedDate, 'YYYYMMDD')

        if (selectedDetail.chargeTypeId == null) {
            toasty.error('Please selected a charge type');
            return false;
        }

        if (selectedDetail.equipmentId == null &&
            (selectedContractChargeType.accountCodingTypeId == 2 || selectedContractChargeType.accountCodingTypeId == 5 || selectedContractChargeType.accountCodingTypeId == 4)) {
            toasty.error('Please select equipment');
            return false;
        }

        if (selectedDetail.chargeTypeUnitsName === 'Hours') {
            // Block end time being earlier than start time (same-day comparison).
            // getDuration treats end-before-start as a midnight-crossing shift and
            // adds a day, so we have to guard separately here.
            //if (selectedDetail.start && selectedDetail.end) {
            //    const startMoment = moment(selectedDetail.start, ['H:mm', 'HH:mm']);
            //    const endMoment = moment(selectedDetail.end, ['H:mm', 'HH:mm']);

            //    if (startMoment.isValid() && endMoment.isValid() && endMoment.isBefore(startMoment)) {
            //        toasty.error('End time cannot be before the start time.');
            //        return false;
            //    }
            //}

            // Validate the entry itself.  A valid range will have a duration > 0.
            const rangeDuration = this.getDuration(
                timesheet.date,
                selectedDetail.start,
                selectedDetail.end,
                selectedDetail.breakStart,
                selectedDetail.breakEnd,
                false,
            );
            if (rangeDuration <= 0) {
                toasty.error('Please enter a valid time range.');
                return false;
            }

            // If we are not editing, attempt to push the entry.
            if (creatingNew) {
                // RLC: Check if new entry will overlap.
                if (
                    !this.timeEntryOverlaps({ ...selectedDetail }, [
                        ...timesheet.timeEntries,
                    ])
                ) {
                    selectedDetail.value = rangeDuration;
                    timesheet.timeEntries.push({ ...selectedDetail });
                } else {
                    toasty.error(
                        'This time entry overlaps entries you\'ve already added for this date.',
                    );
                    return false;
                }

                // Otherwise, check to see if the edit will cause a time overlap.
            } else {
                // RLC: Spread and splice, to avoid mutating state while we check the newly modified entry.
                //     Remove the edited entry from the array first, only compare against the other entries.
                const toCheck = [...timesheet.timeEntries];
                toCheck.splice(editingTimeIndex, 1);

                if (
                    !this.timeEntryOverlaps({ ...selectedDetail }, [
                        ...toCheck,
                    ])
                ) {
                    // We are all good, overwrite the new state.
                    timesheet.id = selectedDetail.timesheetId;
                    timesheet.equipmentId = selectedDetail.equipmentId;
                    timesheet.equipmentName = selectedDetail.equipmentName;
                    selectedDetail.value = rangeDuration;

                    timesheet.timeEntries.push({
                        ...selectedDetail,
                    });
                } else {
                    toasty.error(
                        'This time entry overlaps entries you\'ve already added for this date.',
                    );
                    return false;
                }
            }
        } else if (creatingNew) timesheet.timeEntries.push({ ...selectedDetail });
        else timesheet.timeEntries[editingTimeIndex] = { ...selectedDetail };

        if (creatingNew) {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.selfDispatchTimesheet.create(),
                { ...timesheet },
                'Error Creating Timesheet',
            );
        }

        else {
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.updateInternalCharge(),
                { ...timesheet },
                'Error Saving Timesheet',
            );
        }

        this.setState({
            timesheet,
            selectedDetail: null,
            showAddChargeModal: false,
        });

        this.populateState();
    };

    setSaving = (b) => this.setState({ saving: b });

    onRemoveInternalChargeClicked = (ev, id) => {

        ev.stopPropagation(); 

        this.setState({
            removeChargeId: id,
            showRemoveInternalChargeModal: true
        });
    }

    toggleRemoveChargeModal = () => {
        const { showRemoveInternalChargeModal } = { ...this.state };

        this.setState({
            showRemoveInternalChargeModal: !showRemoveInternalChargeModal
        });
    }

    onRemoveInternalCharge = async () => {
        const { removeChargeId } = { ...this.state };

        await util.fetch.post(ApiRoutes.dailyDrivingLog.removeCharge(removeChargeId));

        this.setState({
            removeChargeId: null,
            showRemoveInternalChargeModal: false
        });

        this.refresh();
    }

    openTimesheet = (e) => {

    };

    calcTime = (startDate, endDate) => {
        const { selectedDate } = { ...this.state };

        if (startDate && endDate) {

            var start = moment(startDate).format('h:mm a');
            var end = moment(endDate).format('h:mm a');

            return this.getDuration(selectedDate, start, end, null, null, true);
        }

        return '0h 00m';
    }

    getTotalChargeHours = () => {
        const { dailyEntries, selectedDate } = { ...this.state };

        var totalMinutes = 0;

        const charges = dailyEntries.filter((x) => x.logEntryType == 2);
        for (var ic = 0; ic < charges.length; ic++) {
            var internalCharge = charges[ic];
            if (internalCharge.removed) {
                continue;
            }

            totalMinutes += this.getDuration(selectedDate, internalCharge.startTime, internalCharge.endTime);
        }

        const hours = Math.floor(totalMinutes / 60);
        const mins = (totalMinutes % 60).toString().padStart(2, '0');
        return `${hours}h ${mins}m`;
    }

    // Computes a single job's total hours the same way its Hours grid
    // displays them in the Totals row. Tonnage scale tickets pair starts
    // with ends across rendered charges (resourceTypeId == 2, !removed);
    // hourly scale tickets sum each rendered charge's start-to-end. Both
    // handle midnight-crossing entries.
    // Hourly section total: sum the stored value on each hourly ticket's
    // parent (resourceTypeId == 1) charges. Single source of truth shared by
    // the hourly grid footer and the header total.
    getJobHourlyHours = (job) => {
        var total = 0;
        (job.scaleTickets ?? []).forEach((st) => {
            if (st.isTonnage == true) return;
            (st.charges ?? []).forEach((c) => {
                if (c.resourceTypeId == 1 && c.removed != true) {
                    total += (c.value || 0);
                }
            });
        });
        return total;
    }

    // Tonnage section total: the whole tonnage grouping is one combined range,
    // from the earliest equipment (resourceTypeId == 2) start to the latest
    // such end. Single source of truth shared by the tonnage grid footer and
    // the header total.
    // Build time ranges from a chronological sequence of tonnage charges.
    // Each charge carries a start; a charge that also carries an end closes the
    // currently open range at that end. After a range closes, the next start
    // opens a new range. So a sequence of starts followed by an end forms one
    // span (first start -> that end), and a later start begins the next span.
    // Returns an array of { start: moment, end: moment }, sorted chronologically.
    mergeTonnageRanges = (charges) => {
        const events = [];
        (charges ?? []).forEach((c) => {
            if (c.start == null) return;
            events.push({
                start: moment(c.start),
                end: c.end != null ? moment(c.end) : null,
            });
        });
        if (events.length === 0) return [];

        events.sort((a, b) => a.start.valueOf() - b.start.valueOf());

        const ranges = [];
        let openStart = null;
        events.forEach((e) => {
            // First start of a new span sets the range's start.
            if (openStart == null) openStart = e.start.clone();
            // A charge carrying an end closes the currently open span.
            if (e.end != null) {
                const end = e.end.clone();
                // Handle an end that falls before the span start (crossed midnight).
                while (end.isBefore(openStart)) end.add(1, 'day');
                ranges.push({ start: openStart, end });
                openStart = null;
            }
        });
        // A trailing start with no closing end leaves an open (unclosed) span;
        // it contributes no measurable hours, so it is not emitted as a range.
        return ranges;
    }

    getJobTonnageHours = (job) => {
        const charges = [];
        (job.scaleTickets ?? []).forEach((st) => {
            if (st.isTonnage != true) return;
            (st.charges ?? []).forEach((c) => {
                if (c.resourceTypeId != 2 || c.removed == true) return;
                charges.push(c);
            });
        });
        const ranges = this.mergeTonnageRanges(charges);
        return ranges.reduce(
            (sum, r) => sum + moment.duration(r.end.diff(r.start)).asHours(),
            0,
        );
    }

    getJobTotalHours = (job) => {
        return this.getJobHourlyHours(job) + this.getJobTonnageHours(job);
    }

    // Internal Charges section total: sum stored value (hours) for each
    // non-removed internal charge.
    getInternalChargesHours = () => {
        const { dailyEntries } = { ...this.state };
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);
        return charges.reduce(
            (sum, c) => sum + (c.removed == true ? 0 : (c.value || 0)),
            0,
        );
    }

    getTotalHours = () => {
        const { dailyEntries } = { ...this.state };
        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        // Sum the same section totals shown in each grid footer, plus internal
        // charges, so the header total always agrees with the sections.
        const totalHours = this.getInternalChargesHours()
            + jobs.reduce((sum, j) => sum + this.getJobTotalHours(j), 0);

        return totalHours.toFixed(2);
    }

    // Ported from TimesheetDetailsForm.getTotalTonnageHours.
    // Accepts a flat array of items shaped { isTonnage, startTime, endTime }.
    // Filters to tonnage entries, then walks them in order pairing each
    // unmatched startTime with the next endTime, accumulating durations.
    // Midnight-crossing entries are handled by rolling the end forward
    // a day at a time until it's after the start.
    getTotalTonnageHours = (timesheets) => {

        const tonnageTimesheets = timesheets.filter((x) => x.isTonnage == true);

        var totalHours = 0.0;
        var start = null;
        for (var i = 0; i < tonnageTimesheets.length; i++) {
            var ts = tonnageTimesheets[i];

            if (start == null && ts.startTime != null) {
                start = ts.startTime;
            }

            if (ts.endTime != null) {
                var startDt = moment(start);
                var endDt = moment(ts.endTime);

                while (endDt.isBefore(startDt)) {
                    endDt.add(1, 'day');
                }

                var duration = moment.duration(endDt.diff(startDt));
                totalHours += duration.asHours();

                start = null;
            }
        }

        if (isNaN(totalHours))
            totalHours = 0;

        return totalHours;
    }

    // A single job's Tonnage total as the Hours grid shows it: sum c.value
    // for rendered charges (resourceTypeId == 2, !removed) with
    // chargeTypeUnitsId == 3.
    getJobTotalTonnage = (job) => {
        var total = 0;
        (job.scaleTickets ?? []).forEach((st) => {
            (st.charges ?? []).forEach((c) => {
                if (c.chargeTypeUnitsId == 3 && c.removed != true) {
                    total += (c.tonnage || 0);
                }
            });
        });
        return total;
    }

    // A single job's Loads total as the Hours grid shows it: tonnage tickets
    // count 1 load per rendered row, non-tonnage tickets sum c.loads on
    // chargeTypeUnitsId == 1 rendered charges.
    getJobTotalLoads = (job) => {
        var total = 0;
        (job.scaleTickets ?? []).forEach((st) => {
            if (st.isDeleted) return;
            (st.charges ?? []).forEach((c) => {
                if (c.chargeTypeUnitsId != 1 || c.removed == true) return;
                if (st.isTonnage == true) {
                    total += 1;
                } else {
                    total += (c.loads || 0);
                }
            });
        });
        return total;
    }

    getTotalTonnage = () => {
        const { dailyEntries } = { ...this.state };
        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);
        return jobs.reduce((sum, j) => sum + this.getJobTotalTonnage(j), 0);
    }

    getTotalLoads = () => {
        const { dailyEntries } = { ...this.state };
        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);
        return jobs.reduce((sum, j) => sum + this.getJobTotalLoads(j), 0);
    }

    // Shared cell styles for the job grids.
    gridStyles = () => ({
        th: { textAlign: 'left', padding: '8px 16px', fontWeight: 500, color: '#6b6b6b', fontSize: '13px' },
        thRight: { textAlign: 'right', padding: '8px 16px', fontWeight: 500, color: '#6b6b6b', fontSize: '13px' },
        td: { padding: '8px 16px', color: '#1a1a1a', fontSize: '13px' },
        tdRight: { padding: '8px 16px', color: '#1a1a1a', fontSize: '13px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    });

    // Parse the job's timesheet number for display: drop the 3rd of 4
    // dash-separated segments (A-B-C-D -> A-B-D). Other shapes pass through.
    formatTicketNumber = (timesheetNumber) => {
        if (!timesheetNumber) return '';
        const parts = String(timesheetNumber).split('-');
        if (parts.length === 4) {
            return [parts[0], parts[1], parts[3]].join('-');
        }
        return String(timesheetNumber);
    };

    // Hourly grouping: tickets where isTonnage == false. Deals with loads,
    // never tonnage. The parent time/hours row is the resourceTypeId == 1
    // charge (hours come from its value). The resourceTypeId == 2 charges are
    // the linked load rows, shown beneath with loads but no time/hours.
    renderHourlyGrid = (job) => {
        const s = this.gridStyles();
        const DASH = '\u2014';

        const tickets = (job.scaleTickets ?? []).filter((st) => st.isTonnage != true);
        if (tickets.length === 0) return { total: 0, grid: null };

        // Sort tickets by their parent (resourceTypeId == 1) charge start time.
        const parentStart = (st) => {
            const parent = (st.charges ?? []).find(
                (c) => c.resourceTypeId == 1 && c.removed != true && c.start != null,
            );
            return parent ? moment(parent.start).valueOf() : Number.POSITIVE_INFINITY;
        };
        tickets.sort((a, b) => parentStart(a) - parentStart(b));

        const rows = [];
        tickets.forEach((st) => {
            const parents = [];
            const loads = [];
            (st.charges ?? []).forEach((c) => {
                if (c.removed == true) return;
                if (c.resourceTypeId == 1) {
                    parents.push(c);
                } else if (c.resourceTypeId == 2) {
                    loads.push(c);
                }
            });

            // Each ticket's parent (resourceTypeId == 1) carries time + hours.
            // Merge the first load's material/loads onto the parent line so the
            // first printed row shows both; remaining loads get their own rows.
            parents.forEach((c, pIdx) => {
                // Only the first parent absorbs the first load (a ticket
                // normally has a single parent).
                const firstLoad = pIdx === 0 ? loads[0] : undefined;
                rows.push({
                    kind: 'parent',
                    ticketNumber: this.formatTicketNumber(st.timesheetNumber),
                    ticketDeleted: st.isDeleted == true,
                    start: c.start,
                    end: c.end,
                    materialName: firstLoad ? (firstLoad.materialName || '') : (c.materialName || ''),
                    chargeTypeUnitsId: c.chargeTypeUnitsId,
                    hours: c.value || 0,
                    loads: (firstLoad && firstLoad.chargeTypeUnitsId == 1) ? (firstLoad.loads || 0) : null,
                });
            });

            // Remaining loads (all but the one merged into the parent).
            const startIdx = parents.length > 0 ? 1 : 0;
            loads.slice(startIdx).forEach((c) => {
                rows.push({
                    kind: 'load',
                    ticketNumber: st.workOrderNumber,
                    ticketDeleted: st.isDeleted == true,
                    start: c.start,
                    end: c.end,
                    materialName: c.materialName || '',
                    chargeTypeUnitsId: c.chargeTypeUnitsId,
                    hours: null,
                    loads: (c.chargeTypeUnitsId == 1) ? (c.loads || 0) : null,
                });
            });
        });

        if (rows.length === 0) return { total: 0, grid: null };

        const totalLoads = rows.reduce(
            (sum, r) => sum + (r.ticketDeleted ? 0 : (r.loads || 0)),
            0,
        );
        // Sum the hours rendered in the grid rows so the footer total and the
        // returned total are computed from the exact same source.
        const totalHours = rows.reduce(
            (sum, r) => sum + (r.hours != null ? r.hours : 0),
            0,
        );

        // Build the list of time ranges for the section header. Each parent row
        // carries its own start/end; load rows mirror the parent and are
        // skipped. Listed per-row, comma separated, with no merging.
        const rangeDisplay = rows
            .filter((r) => r.kind === 'parent' && (r.start != null || r.end != null))
            .map((r) => {
                const startStr = r.start ? moment(r.start).format('h:mm a') : '';
                const endStr = r.end ? moment(r.end).format('h:mm a') : '';
                if (startStr && endStr) return startStr + ' - ' + endStr;
                return startStr || endStr;
            })
            .join(', ');

        const grid = (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff' }}>
                <thead>
                    <tr style={{ background: '#f3f3f3', borderBottom: '0.5px solid #cccccc' }}>
                        <th style={s.th}>Ticket #</th>
                        <th style={s.th}>Time</th>
                        <th style={s.th}>Materials</th>
                        <th style={s.thRight}>Loads</th>
                        <th style={s.thRight}>Hours</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, ridx) => {
                        const startStr = r.start ? moment(r.start).format('h:mm a') : '';
                        const endStr = r.end ? moment(r.end).format('h:mm a') : '';
                        let timeDisplay = '';
                        if (startStr && endStr) timeDisplay = startStr + ' - ' + endStr;
                        else if (startStr) timeDisplay = startStr;
                        else if (endStr) timeDisplay = endStr;
                        // Load rows mirror the parent's time; don't repeat it.
                        if (r.kind === 'load') timeDisplay = '';

                        const hoursDisplay = r.hours != null ? r.hours.toFixed(2) : DASH;
                        const loadsDisplay = r.loads != null ? r.loads : DASH;
                        const isLast = ridx === rows.length - 1;
                        // The number is the ticket's parsed timesheet number; show it
                        // on parent rows, leave load rows blank to avoid repetition.
                        const numberDisplay = r.kind === 'parent' ? (r.ticketNumber || DASH) : '';

                        return (
                            <tr key={ridx} style={{ borderBottom: isLast ? '0.5px solid #cccccc' : '0.5px solid #ededed' }}>
                                <td style={s.td}>{numberDisplay}</td>
                                <td style={s.td}>{timeDisplay}</td>
                                <td style={s.td}>{r.materialName}</td>
                                <td style={s.tdRight}>{loadsDisplay}</td>
                                <td style={s.tdRight}>{hoursDisplay}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#f3f3f3', fontWeight: 800 }}>
                        <td style={{ ...s.td, fontWeight: 800 }}>Totals</td>
                        <td style={{ ...s.td, fontWeight: 800 }}>{rangeDisplay}</td>
                        <td style={{ ...s.td, fontWeight: 800 }}></td>
                        <td style={{ ...s.tdRight, fontWeight: 800 }}>{totalLoads}</td>
                        <td style={{ ...s.tdRight, fontWeight: 800 }}>{totalHours.toFixed(2)}</td>
                    </tr>
                </tfoot>
            </table>
        );

        return { total: totalHours, ranges: rangeDisplay, grid };
    };

    // Tonnage grouping: tickets where isTonnage == true. Deals with tonnage,
    // never loads. Hours are a range: a start-time row paired with the next
    // end-time row. Unit-3 charges carry the tonnage; linked children and
    // unit-3 charges are excluded from the hours pairing (old logic).
    renderTonnageGrid = (job) => {
        const s = this.gridStyles();
        const DASH = '\u2014';

        const tickets = (job.scaleTickets ?? []).filter((st) => st.isTonnage == true);
        if (tickets.length === 0) return { total: 0, grid: null };

        // Sort tickets by their equipment (resourceTypeId == 2) charge start time.
        const equipStart = (st) => {
            const eq = (st.charges ?? []).find(
                (c) => c.resourceTypeId == 2 && c.removed != true && c.start != null,
            );
            return eq ? moment(eq.start).valueOf() : Number.POSITIVE_INFINITY;
        };
        tickets.sort((a, b) => equipStart(a) - equipStart(b));

        const rows = [];
        tickets.forEach((st) => {
            (st.charges ?? []).forEach((c) => {
                if (c.resourceTypeId != 2 || c.removed == true) return;
                rows.push({
                    ticketNumber: this.formatTicketNumber(st.timesheetNumber),
                    ticketDeleted: st.isDeleted == true,
                    start: c.start,
                    end: c.end,
                    materialName: c.materialName || '',
                    chargeTypeUnitsId: c.chargeTypeUnitsId,
                    linkedChargeTypeId: c.linkedChargeTypeId,
                    tonnage: c.tonnage,
                });
            });
        });

        if (rows.length === 0) return { total: 0, grid: null };

        // Build ranges from the same charges the rows render. Each tonnage
        // charge carries a start; the charge that also has an end closes the
        // range. A later start after a closed range begins a new range, so
        // separate spans render as separate ranges and the hours total excludes
        // the gaps. Uses the same helper getJobTonnageHours uses, so the footer
        // total and the returned total always agree.
        const rangeCharges = rows
            .filter((r) => r.start != null)
            .map((r) => ({ start: r.start, end: r.end }));
        const ranges = this.mergeTonnageRanges(rangeCharges);

        const totalHours = ranges.reduce(
            (sum, r) => sum + moment.duration(r.end.diff(r.start)).asHours(),
            0,
        );

        const totalTonnage = rows.reduce(
            (sum, r) => sum + (!r.ticketDeleted ? (r.tonnage || 0) : 0),
            0,
        );

        const timeRangeDisplay = ranges.length > 0
            ? ranges
                .map((r) => r.start.format('h:mm a') + ' - ' + r.end.format('h:mm a'))
                .join(', ')
            : '';

        // Assign each row to the range it belongs to so the grid can visibly
        // group rows under their range. A row belongs to a range when its start
        // falls within [range.start, range.end]. Rows that fall outside every
        // range (e.g. a trailing open start with no closing end) collect into a
        // trailing "ungrouped" bucket rendered without a range header.
        const groups = ranges.map((r) => ({ range: r, rows: [] }));
        const ungrouped = [];
        rows.forEach((r) => {
            if (r.start == null) {
                ungrouped.push(r);
                return;
            }
            const m = moment(r.start);
            const gi = ranges.findIndex(
                (rng) => m.isSameOrAfter(rng.start) && m.isSameOrBefore(rng.end),
            );
            if (gi >= 0) groups[gi].rows.push(r);
            else ungrouped.push(r);
        });

        const grid = (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff' }}>
                <thead>
                    <tr style={{ background: '#f3f3f3', borderBottom: '0.5px solid #cccccc' }}>
                        <th style={s.th}>Ticket #</th>
                        <th style={s.th}>Time</th>
                        <th style={s.th}>Materials</th>
                        <th style={s.thRight}>Tonnage</th>
                        <th style={s.thRight}>Hours</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g, gi) => {
                        const rangeHours = moment.duration(g.range.end.diff(g.range.start)).asHours();
                        const rangeLabel = g.range.start.format('h:mm a') + ' - ' + g.range.end.format('h:mm a');
                        const groupTonnage = g.rows.reduce(
                            (sum, r) => sum + (!r.ticketDeleted ? (r.tonnage || 0) : 0),
                            0,
                        );
                        return (
                            <React.Fragment key={'g' + gi}>
                                <tr style={{ background: '#eef3f8', borderTop: '0.5px solid #cccccc', borderBottom: '0.5px solid #e0e0e0' }}>
                                    <td style={{ ...s.td, fontWeight: 700, color: '#3a4a5a' }} colSpan={3}>
                                        {rangeLabel}
                                    </td>
                                    <td style={{ ...s.tdRight, fontWeight: 700, color: '#3a4a5a' }}>{groupTonnage.toFixed(2)}</td>
                                    <td style={{ ...s.tdRight, fontWeight: 700, color: '#3a4a5a' }}>{rangeHours.toFixed(2)}</td>
                                </tr>
                                {g.rows.map((r, ridx) => {
                                    const startStr = r.start ? moment(r.start).format('h:mm a') : '';
                                    const endStr = r.end ? moment(r.end).format('h:mm a') : '';
                                    let timeDisplay = '';
                                    if (startStr && endStr) timeDisplay = startStr + ' - ' + endStr;
                                    else if (startStr) timeDisplay = startStr;
                                    else if (endStr) timeDisplay = endStr;

                                    const tonnageDisplay = r.tonnage != null ? Number(r.tonnage).toFixed(2) : DASH;
                                    const isLast = ridx === g.rows.length - 1;

                                    return (
                                        <tr key={'g' + gi + 'r' + ridx} style={{ borderBottom: isLast ? '0.5px solid #cccccc' : '0.5px solid #ededed' }}>
                                            <td style={{ ...s.td, paddingLeft: '32px' }}>{r.ticketNumber && r.ticketNumber.length > 0 ? r.ticketNumber : DASH}</td>
                                            <td style={s.td}>{timeDisplay}</td>
                                            <td style={s.td}>{r.materialName}</td>
                                            <td style={s.tdRight}>{tonnageDisplay}</td>
                                            <td style={s.tdRight}>{DASH}</td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                    {ungrouped.length > 0 && ungrouped.map((r, ridx) => {
                        const startStr = r.start ? moment(r.start).format('h:mm a') : '';
                        const endStr = r.end ? moment(r.end).format('h:mm a') : '';
                        let timeDisplay = '';
                        if (startStr && endStr) timeDisplay = startStr + ' - ' + endStr;
                        else if (startStr) timeDisplay = startStr;
                        else if (endStr) timeDisplay = endStr;

                        const tonnageDisplay = r.tonnage != null ? Number(r.tonnage).toFixed(2) : DASH;
                        const isLast = ridx === ungrouped.length - 1;

                        return (
                            <tr key={'u' + ridx} style={{ borderBottom: isLast ? '0.5px solid #cccccc' : '0.5px solid #ededed' }}>
                                <td style={s.td}>{r.ticketNumber && r.ticketNumber.length > 0 ? r.ticketNumber : DASH}</td>
                                <td style={s.td}>{timeDisplay}</td>
                                <td style={s.td}>{r.materialName}</td>
                                <td style={s.tdRight}>{tonnageDisplay}</td>
                                <td style={s.tdRight}>{DASH}</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#f3f3f3', fontWeight: 800 }}>
                        <td style={{ ...s.td, fontWeight: 800 }}>Totals</td>
                        <td style={{ ...s.td, fontWeight: 800 }}>{timeRangeDisplay}</td>
                        <td style={{ ...s.td, fontWeight: 800 }}></td>
                        <td style={{ ...s.tdRight, fontWeight: 800 }}>{totalTonnage.toFixed(2)}</td>
                        <td style={{ ...s.tdRight, fontWeight: 800 }}>{totalHours.toFixed(2)}</td>
                    </tr>
                </tfoot>
            </table>
        );

        return { total: totalHours, ranges: timeRangeDisplay, grid };
    };

    render() {
        if (this.state.loading) {
            return (
                <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting log...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }
        return this.renderForm();
    }

    renderForm() {

        const {
            dailyEntries,
            saving,
            jobsOpen,
            chargesOpen,
            selectedDate,
            showAddChargeModal,
            addTimeFormValidated,
            selectedDetail,
            contracts,
            timesheet,
            editingTimeIndex,
            showSubmitChargesModal,
            summaryJobsOpen,
            summaryChargesOpen,
            showRemoveInternalChargeModal,
            equipment,
            driverName
        } = { ...this.state };

        var formattedDate = moment(selectedDate, 'YYYYMMDD').format('dddd, MMMM Do YYYY');

        const jobs = dailyEntries.filter((x) => x.logEntryType == 1);
        const charges = dailyEntries.filter((x) => x.logEntryType == 2 && x.removed !== true);
        const unconfirmedCharges = charges.filter((x) => !x.isConfirmed);

        const selectedContract = (contracts ?? []).find(
            (x) => x.id === timesheet?.contractId,
        );

        const selectedEquipment = (equipment ?? []).find(
            (x) => x.id === selectedDetail?.equipmentId,
        );

        const selectedContractChargeType = (selectedContract?.contractChargeTypes ?? []).find(
            (x) => x.chargeTypeId === selectedDetail?.chargeTypeId,
        ) ?? '';

        const selectedIncrement = selectedContractChargeType?.configurations
            ? selectedContractChargeType.configurations[0].increment
            : '';

        const totalTonnage = this.getTotalTonnage();

        return (
            <>
                <div className='dailyDrivingLog confirmationContainer h-100 pt-2'>
                    <Container size="sm">

                        <FormBlocker showProgress show={!!saving} />

                        <div className="title"><span className="fa fa-calendar"></span>&nbsp;&nbsp;{formattedDate}</div>

                        <table style={{ fontSize: '13px', marginBottom: '15px' }}>
                            <tr>
                                <td width='65'><b>Driver:</b></td><td>{driverName}</td>
                            </tr>
                            <tr>
                                <td><b>Hours:</b></td><td>{this.getTotalHours()}</td>
                            </tr>
                            <tr>
                                <td><b>Tonnage:</b></td><td>{totalTonnage.toFixed(2)}</td>
                            </tr>
                            <tr>
                                <td><b>Loads:</b></td><td>{this.getTotalLoads()}</td>
                            </tr>
                        </table>

                        <CollapseUnderlineHeader
                            headerText='Jobs'
                            isOpen={jobsOpen}
                            toggleCollapse={() => this.setState({
                                jobsOpen: !jobsOpen,
                            })}                >
                            {jobs.map((d, idx) => (
                                <>

                                    {d.logEntryType == 1 && (
                                        <Card color={!d.isConfirmed || !d.isComplete ? 'danger' : 'primary'} style={{ overflow: 'hidden' }}>
                                            <CardHeader onClick={() => this.onEntryClicked(d)}>
                                                <table width='100%'>
                                                    <tr>
                                                        <td>
                                                            <span className='entryLogHeader'>{d.customer} - {d.contractNumber}</span>
                                                            
                                                            {d.workOrderNumber.length > 0 &&
                                                                <span className='entryLogHeader'>&nbsp;&nbsp;#{d.workOrderNumber}</span>
                                                            }
                                                        </td>
                                                        <td align='right'>
                                                            <span className='entryLogStatus'>
                                                                {!d.isConfirmed ? 'Unconfirmed' : !d.isComplete ? 'Incomplete' : 'Completed'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </table>
                                            </CardHeader>
                                            <CardBody style={{ paddingTop: '5px', background: '#ffffff' }}>

                                                <div onClick={() => this.onEntryClicked(d)}>
                                                    <table width='100%'>
                                                        <tr>
                                                            <td width='80' className="label" align='right'>Start:</td>
                                                            <td>{moment(d.startDate).format('h:mm a')}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Haul From:</td>
                                                            <td>{d.haulFrom}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Haul To:</td>
                                                            <td>{d.haulTo}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="label" align='right'>Materials:</td>
                                                            <td>{d.materials}</td>
                                                        </tr>
                                                        {d.notes && d.notes.length > 0 &&
                                                            <tr>
                                                                <td className="label" align='right'>Notes:</td>
                                                                <td>{d.notes}</td>
                                                            </tr>
                                                        }
                                                    </table>
                                                </div>

                                                {d?.scaleTickets?.length > 0 &&
                                                    <div style={{ height: '8px' }} />
                                                }

                                                {d?.scaleTickets?.length > 0 && (d.scaleTickets ?? []).some((st) => st.isTonnage != true) &&
                                                    (() => {
                                                        const hourly = this.renderHourlyGrid(d);
                                                        return (
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        cursor: 'pointer',
                                                                        padding: '8px 0 8px 8px',
                                                                        borderBottom: '1px solid #cccccc',
                                                                        fontWeight: 'bold',
                                                                        fontSize: '15px',
                                                                        userSelect: 'none',
                                                                    }}
                                                                    onClick={() => {
                                                                        d.showHourlyJobs = !d.showHourlyJobs;
                                                                        this.setState({ dailyEntries: dailyEntries });
                                                                    }}
                                                                >
                                                                    <i className={d.showHourlyJobs ? 'fa fa-caret-down' : 'fa fa-caret-right'} />
                                                                    <span style={{ flex: '0 0 140px' }}>Hourly Tickets</span>
                                                                    <span style={{ flex: '1 1 auto', textAlign: 'left', fontSize: '13px' }}>
                                                                        {hourly.ranges ? hourly.ranges : ''}
                                                                    </span>
                                                                    <span style={{ flex: '0 0 auto', paddingRight: '4px', fontSize: '13px' }}>Total Hours: {hourly.total.toFixed(2)}</span>
                                                                </div>
                                                                {d.showHourlyJobs && hourly.grid}
                                                            </>
                                                        );
                                                    })()
                                                }

                                                {d?.scaleTickets?.length > 0 && (d.scaleTickets ?? []).some((st) => st.isTonnage == true) &&
                                                    (() => {
                                                        const tonnage = this.renderTonnageGrid(d);
                                                        return (
                                                            <>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        cursor: 'pointer',
                                                                        padding: '8px 0 8px 8px',
                                                                        borderBottom: '1px solid #cccccc',
                                                                        fontWeight: 'bold',
                                                                        fontSize: '15px',
                                                                        userSelect: 'none',
                                                                    }}
                                                                    onClick={() => {
                                                                        d.showTonnageJobs = !d.showTonnageJobs;
                                                                        this.setState({ dailyEntries: dailyEntries });
                                                                    }}
                                                                >
                                                                    <i className={d.showTonnageJobs ? 'fa fa-caret-down' : 'fa fa-caret-right'} />
                                                                    <span style={{ flex: '0 0 140px' }}>Tonnage Tickets</span>
                                                                    <span style={{ flex: '1 1 auto', textAlign: 'left', fontSize: '13px' }}>
                                                                        {tonnage.ranges ? tonnage.ranges : ''}
                                                                    </span>
                                                                    <span style={{ flex: '0 0 auto', paddingRight: '4px', fontSize: '13px' }}>Total Hours: {tonnage.total.toFixed(2)}</span>
                                                                </div>
                                                                {d.showTonnageJobs && tonnage.grid}
                                                            </>
                                                        );
                                                    })()
                                                }


                                                <table width='100%' style={{ marginTop: '8px', marginBottom: '8px' }}>
                                                    <tr>
                                                        <td width='100%' align='right'>
                                                            <SmallButton
                                                                type="button"
                                                                className="ml-1"
                                                                title=""
                                                                onClick={() => this.props.history.push(
                                                                    `${AppNavPaths.Timesheet}/${d.timesheetId}`,
                                                                )}
                                                            >
                                                                <i className="fa fa-plus-circle fa-lg mr-1" />
                                                                Add Charge
                                                            </SmallButton>
                                                        </td>
                                                    </tr>
                                                </table>
                                                

                                            </CardBody>
                                        </Card>
                                    )}
                                </>
                            ))}

                            {jobs.length == 0 &&
                                <div>No Jobs</div>
                            }
                        </CollapseUnderlineHeader>

                        <div>&nbsp;</div>

                        <CollapseUnderlineHeader
                            headerText='Internal Charges'
                            isOpen={chargesOpen}
                            toggleCollapse={() => this.setState({
                                chargesOpen: !chargesOpen,
                            })}>

                            {charges.length == 0 &&
                                <div>No Internal Charges</div>
                            }

                            {charges.length > 0 && (() => {
                                const rowMinutes = (c) => this.getDuration(selectedDate, c.startTime, c.endTime, null, null, false) || 0;
                                const toDecimalHours = (mins) => (mins / 60).toFixed(2);
                                const totalMinutes = charges.reduce((sum, c) => sum + rowMinutes(c), 0);
                                const totalDecimal = toDecimalHours(totalMinutes);

                                const thStyle = { textAlign: 'left', padding: '8px 16px', fontWeight: 500, color: '#6b6b6b', fontSize: '13px' };
                                const thRightStyle = { ...thStyle, textAlign: 'right' };
                                const tdStyle = { padding: '8px 16px', color: '#1a1a1a', fontSize: '13px' };
                                const tdRightStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

                                return (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#ffffff', marginBottom: '16px' }}>
                                        <thead>
                                            <tr style={{ background: '#f3f3f3', borderBottom: '0.5px solid #cccccc' }}>
                                                <th style={thStyle}>Charge</th>
                                                <th style={thStyle}>Equipment</th>
                                                <th style={thStyle}>Start</th>
                                                <th style={thStyle}>End</th>
                                                <th style={thRightStyle}>Hours</th>
                                                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                                                <th style={thRightStyle}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {charges.map((d, idx) => {
                                                const submitted = d.isConfirmed;
                                                const hoursDecimal = toDecimalHours(rowMinutes(d));
                                                const startDisplay = d.startTime ? moment(d.startTime, ['HH:mm']).format('h:mm a') : '';
                                                const endDisplay = d.endTime ? moment(d.endTime, ['HH:mm']).format('h:mm a') : '';

                                                const iconBtnStyle = (isDelete) => ({
                                                    padding: '6px',
                                                    width: '32px',
                                                    height: '32px',
                                                    marginLeft: isDelete ? '4px' : 0,
                                                    background: submitted ? '#f5f5f5' : '#ffffff',
                                                    border: '0.5px solid rgba(0,0,0,' + (submitted ? '0.1' : '0.18') + ')',
                                                    color: submitted ? '#bdbdbd' : (isDelete ? '#c0392b' : '#1a1a1a'),
                                                    borderRadius: '6px',
                                                    cursor: submitted ? 'not-allowed' : 'pointer',
                                                });

                                                const isLast = idx === charges.length - 1;
                                                return (
                                                    <tr key={d.id ?? idx} style={{ borderBottom: isLast ? '0.5px solid #cccccc' : '0.5px solid #ededed', background: '#ffffff' }}>
                                                        <td style={tdStyle}>{d.chargeTypeName}</td>
                                                        <td style={tdStyle}>{d.equipmentName}</td>
                                                        <td style={tdStyle}>{startDisplay}</td>
                                                        <td style={tdStyle}>{endDisplay}</td>
                                                        <td style={tdRightStyle}>{hoursDecimal}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                background: submitted ? '#1e8449' : '#c0392b',
                                                                color: '#ffffff',
                                                                fontSize: '12px',
                                                                padding: '3px 10px',
                                                                borderRadius: '6px',
                                                            }}>
                                                                {submitted ? 'Submitted' : 'Unsubmitted'}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            <button
                                                                type="button"
                                                                disabled={submitted}
                                                                title="Edit charge"
                                                                onClick={() => this.onEditChargeClicked(idx, d)}
                                                                style={iconBtnStyle(false)}
                                                            >
                                                                <i className="fa fa-edit" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={submitted}
                                                                title="Remove charge"
                                                                onClick={(ev) => this.onRemoveInternalChargeClicked(ev, d.id)}
                                                                style={iconBtnStyle(true)}
                                                            >
                                                                <i className="fa fa-trash" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ background: '#f3f3f3', fontWeight: 800 }}>
                                                <td colSpan="4" style={{ ...tdStyle, fontWeight: 800 }}>Totals</td>
                                                <td style={{ ...tdRightStyle, fontWeight: 800 }}>{totalDecimal}</td>
                                                <td colSpan="2" style={tdStyle}></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                );
                            })()}

                        </CollapseUnderlineHeader>
                    </Container>
                </div>

                <FlexCenterRow className="userActions p-1">

                    {(charges.length == 0 || (charges.length > 0 && unconfirmedCharges.length > 0)) &&
                        <Button
                            type="button"
                            color="success"
                            disabled={saving}
                            id="addChargesButton"
                            onClick={() => this.onAddChargeClicked()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={
                                    saving ? faCircleNotch : faPlus
                                }
                                className="mr-2"
                            />
                            Add Internal Charge
                        </Button>
                    }

                    {unconfirmedCharges.length > 0 && (
                        <Button
                            type="button"
                            color="primary"
                            disabled={saving}
                            onClick={() => this.showSubmitChargesModal()}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={
                                    saving ? faCircleNotch : faCheckCircle
                                }
                                className="mr-2"
                            />
                            Submit Charges
                        </Button>
                    )}
                </FlexCenterRow>

                <Modal className="full-screen-modal"
                    backdrop="static"
                    keyboard={false}
                    isOpen={showSubmitChargesModal}>
                    <ModalHeader>
                        <FlexBetweenRow className="w-100">
                            <span className="title"><span className="fa fa-calendar"></span>&nbsp;&nbsp;{formattedDate}</span>
                            <span
                                className="fa fa-times fa-lg cursor-pointer"
                                onClick={() => this.setState({
                                    showSubmitChargesModal: false,
                                })}
                            />
                        </FlexBetweenRow>

                        
                    </ModalHeader>
                    <ModalBody>

                        <CollapseUnderlineHeader
                            headerText='Jobs'
                            isOpen={summaryJobsOpen}
                            toggleCollapse={() => this.setState({
                                summaryJobsOpen: !summaryJobsOpen,
                            })}>

                            <table width='100%'>
                                {jobs.map((d, idx) => (
                                    <tr>
                                        <td width='100%'>
                                            <span style={{ fontWeight: 'bold' }}>{d.customer} - {d.contractNumber}</span>

                                            <table width='100%'>

                                                {d.scaleTickets.map((st, idx) => (
                                                    <tr>
                                                        <td width='100%'>
                                                            <span>Ticket: #{st.workOrderNumber}:</span>

                                                            <table width='100%'>
                                                                {st.charges.map((c, idx) => (
                                                                    <>
                                                                        {c.chargeTypeUnitsId != 3 &&
                                                                            <tr>
                                                                                <td width='40%'>&nbsp;&nbsp;&nbsp;&nbsp;{c.chargeName}</td>
                                                                                <td width='20%'>{c.start != null ? moment(c.start).format('h:mm a') : '---'}</td>
                                                                                <td width='20%'>{c.end != null ? moment(c.end).format('h:mm a') : '---'}</td>
                                                                               
                                                                            </tr>
                                                                        }
                                                                    </>
                                                                ))}
                                                            </table>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </table>
                                        </td>
                                    </tr>
                                ))}
                            </table>
                            {jobs.length == 0 &&
                                <div>No Jobs</div>
                            }

                        </CollapseUnderlineHeader>
                        <p></p>
                        <CollapseUnderlineHeader
                            headerText='Internal Charges'
                            isOpen={summaryChargesOpen}
                            toggleCollapse={() => this.setState({
                                summaryChargesOpen: !summaryChargesOpen,
                            })}>

                            <table width='100%'>
                                <tr>
                                    <td width='100%'>
                                        <table width='100%'>
                                            <tr>
                                                <td width='100%'>
                                                    <table width='100%'>
                                                        {charges.map((d, idx) => (
                                                            <>
                                                                <tr>
                                                                    <td width='40%'>{d.chargeTypeName}</td>
                                                                    <td width='20%'>{moment(d.startTime, ['HH:mm']).format('h:mm a')}</td>
                                                                    <td width='20%'>{moment(d.endTime, ['HH:mm']).format('h:mm a')}</td>
                                                            
                                                                </tr>
                                                            </>
                                                        ))}
                                                    </table>

                                                </td>
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                            </table>
                            
                            {charges.length == 0 &&
                                <div>No Charges</div>
                            }

                        </CollapseUnderlineHeader>


                    </ModalBody>
                    <ModalFooter>

                        <table width='100%' style={{ marginBottom: '8px' }}>
                            <tr>
                                <td width='100%' align='center' style={{ fontSize: '20px', fontWeight: 'bold' }}>Total Hours: {this.getTotalChargeHours()}</td>
                            </tr>
                        </table>

                        <FlexColumnStart className="w-100">

                            <FlexCenterRow className="w-100">
                                <Button
                                    type="button"
                                    color="success"
                                    disabled={saving}
                                    onClick={() => this.submitInternalCharges()}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={
                                            saving ? faCircleNotch : faCheckCircle
                                        }
                                        className="mr-2"
                                    />
                                    Submit Charges
                                </Button>
                            </FlexCenterRow>

                        </FlexColumnStart>
                    </ModalFooter>
                </Modal>

                <Modal
                    className="full-screen-modal"
                    backdrop="static"
                    keyboard={false}
                    isOpen={showAddChargeModal}
                >
                    <ModalHeader>
                        <FlexBetweenRow className="w-100">
                            <span>
                                {!Number.isNaN(parseInt(editingTimeIndex))
                                    ? 'Edit Internal Charge'
                                    : 'Add Internal Charge'}
                            </span>
                            <span
                                className="fa fa-times fa-lg cursor-pointer"
                                onClick={() => this.setState({
                                    showAddChargeModal: false,
                                })}
                            />
                        </FlexBetweenRow>
                    </ModalHeader>
                    <FormValidated
                        className="modal-body add-self-dispatch-time-form d-flex flex-column h-100 min-height-0"
                        id="addSelfDispatchTimeForm"
                        name="addSelfDispatchTimeForm"
                        ref={this.addTimeFormRef}
                        onSubmit={this.onAddTimeSubmit}
                        setIsValidated={(validated) => this.setState({ addTimeFormValidated: validated })}
                        isValidated={addTimeFormValidated}
                    >
                        <div className="d-flex flex-fill flex-column overflow-y-auto time-entry-content">
                            <FormGroup>
                                <FormLabel
                                    htmlFor="chargeTypesDropdown"
                                    text="Charge"
                                    required
                                />
                                <ValidatedSelect
                                    menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                                    maxMenuHeight={150}
                                    isClearable={false}
                                    name="chargeTypesDropdown"
                                    required
                                    isDisabled={!!selectedDetail?.id}
                                    value={selectedContractChargeType}
                                    options={
                                        selectedContract?.contractChargeTypes
                                        ?? []
                                    }
                                    onChange={this.onChargeTypeChanged}
                                    getOptionLabel={(o) => o.description}
                                    getOptionValue={(o) => o.chargeTypeId}
                                    validationMessage="A charge is required."
                                />
                            </FormGroup>

                            {selectedContractChargeType?.id > 0
                                && (selectedContractChargeType.accountCodingTypeId == 2 || selectedContractChargeType.accountCodingTypeId == 5 || selectedContractChargeType.accountCodingTypeId == 4) &&
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="equipmentDropdown"
                                        text="Equipment"
                                        required
                                    />
                                    <ValidatedSelect
                                        menuPosition="fixed" /* RLC: When being in an overflow container, this will prevent cutoff. */
                                        maxMenuHeight={150}
                                        isClearable={false}
                                        name="equipmentDropdown"
                                        required
                                        value={selectedEquipment}
                                        options={
                                            equipment
                                            ?? []
                                        }
                                        getOptionLabel={(o) => o.description}
                                        getOptionValue={(o) => o.id}
                                        validationMessage="Equipment is required."
                                        onChange={this.onEquipmentChanged}
                                    />
                                </FormGroup>
                            }

                            {selectedContractChargeType?.chargeUnits
                                === 'Hours' && (
                                <>
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0"
                                            htmlFor="start"
                                            text="Start Time"
                                        />
                                        <FlexColumnStart className="w-100">
                                            <TimeEntry
                                                name="start"
                                                value={selectedDetail.start}
                                                increment={selectedIncrement}
                                                onChange={this.onChargeChange}
                                            />
                                        </FlexColumnStart>
                                    </FormGroup>
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0 flex-fill"
                                            htmlFor="chargeEndTime"
                                            text="End Time"
                                        />
                                        <FlexColumnStart className="w-100">
                                            <TimeEntry
                                                name="end"
                                                value={selectedDetail.end}
                                                increment={selectedIncrement}
                                                onChange={this.onChargeChange}
                                            />
                                        </FlexColumnStart>
                                    </FormGroup>

                                    <div className="p-0 mb-2 border-top" />
                                    <FormGroup className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                                        <FormLabel
                                            className="mb-0 w-100"
                                            htmlFor="totalDuration"
                                            text="Total"
                                            required={false}
                                        />
                                        <div className="d-flex flex-row flex-nowrap align-items-center justify-content-start">
                                            <span
                                                id="totalDuration"
                                                className="h3 font-weight-bold text-muted m-0"
                                            >
                                                {this.getDuration(
                                                    timesheet?.date,
                                                    selectedDetail?.start,
                                                    selectedDetail?.end,
                                                    selectedDetail?.breakStart,
                                                    selectedDetail?.breakEnd,
                                                    true,
                                                )}
                                            </span>
                                        </div>
                                    </FormGroup>
                                    <div className="p-0 mb-2 border-top" />
                                </>
                            )}
                        </div>
                        <FlexColumnStart className="w-100">
                         
                            <FlexCenterRow className="w-100">
                                <Button
                                    name="addSelfDispatchTimeForm"
                                    className="w-100 d-flex flex-row flex-nowrap align-items-center justify-content-center"
                                    color="primary"
                                    onClick={this.onUpdateCharge}
                                >
                                    Save Charge
                                </Button>
                            </FlexCenterRow>
                            
                        </FlexColumnStart>
                    </FormValidated>
                </Modal>

                <Modal
                    isOpen={showRemoveInternalChargeModal}
                    toggle={this.toggleRemoveChargeModal}>

                    <ModalHeader toggle={this.toggleRemoveChargeModal}>
                        Remove Internal Charge
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <p className="text-danger">
                                Are you sure you wish to remove this charge?
                            </p>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="danger"
                            onClick={
                                this.onRemoveInternalCharge
                            }
                        >
                            Remove
                        </Button>
                        {' '}
                        <Button
                            color="secondary"
                            onClick={
                                this.toggleRemoveChargeModal
                            }
                        >
                            Cancel
                        </Button>
                        {' '}
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

export default withRouter(DailyDrivingLogForm);